/**
 * StalenessQueue
 *
 * Component for viewing and managing stale content that needs regeneration.
 *
 * Part of Story 30: Domain Knowledge Refinement System (Task 30.10)
 *
 * Date: December 18, 2025
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from '@/components/ui/use-toast';
import {
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Loader2,
  ThumbsUp,
  ThumbsDown,
  Minus,
  Filter,
  RotateCcw,
} from 'lucide-react';
import { format } from 'date-fns';
import { useSite } from '@/contexts/SiteContext';
import {
  getStalenessQueue,
  getStalenessStats,
  validateStaleness,
  dismissStaleness,
  StalenessRecord,
  StalenessFilters,
} from '@/services/domainKnowledgeService';

interface StalenessQueueProps {
  refreshTrigger?: number;
}

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'regenerating', label: 'Regenerating' },
  { value: 'pending_validation', label: 'Pending Validation' },
  { value: 'validated', label: 'Validated' },
  { value: 'dismissed', label: 'Dismissed' },
];

const CONTENT_TYPES = [
  { value: 'all', label: 'All Types' },
  { value: 'product_aeo', label: 'Product AEO' },
  { value: 'category_aeo', label: 'Category AEO' },
];

const statusColors: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  regenerating: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  pending_validation: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  validated: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  dismissed: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
};

const validationIcons: Record<string, React.ReactNode> = {
  improved: <ThumbsUp className="h-4 w-4 text-green-600" />,
  no_change: <Minus className="h-4 w-4 text-gray-600" />,
  worse: <ThumbsDown className="h-4 w-4 text-red-600" />,
};

export function StalenessQueue({ refreshTrigger }: StalenessQueueProps) {
  const { selectedSite } = useSite();
  const [items, setItems] = useState<StalenessRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<Record<string, number>>({});
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    total: 0,
  });

  const [filters, setFilters] = useState<StalenessFilters>({
    siteId: selectedSite?.id || undefined,
    status: 'pending',
    page: 1,
    limit: 20,
  });

  // Validation dialog state
  const [validationDialog, setValidationDialog] = useState<{
    open: boolean;
    item: StalenessRecord | null;
    action: 'improved' | 'no_change' | 'worse' | 'dismiss' | null;
  }>({
    open: false,
    item: null,
    action: null,
  });

  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Update site filter when site changes
  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      siteId: selectedSite?.id || undefined,
      page: 1,
    }));
  }, [selectedSite?.id]);

  // Load data when filters change
  useEffect(() => {
    loadQueue();
    loadStats();
  }, [filters, refreshTrigger]);

  const loadQueue = async () => {
    setLoading(true);
    try {
      const response = await getStalenessQueue(filters);
      if (response.success && response.data) {
        setItems(response.data.items);
        setPagination({
          page: response.data.page,
          totalPages: response.data.totalPages,
          total: response.data.total,
        });
      }
    } catch (err) {
      console.error('[StalenessQueue] Error loading:', err);
      toast({
        title: 'Error',
        description: 'Failed to load staleness queue',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await getStalenessStats(selectedSite?.id);
      if (response.success && response.data) {
        setStats(response.data);
      }
    } catch (err) {
      console.error('[StalenessQueue] Error loading stats:', err);
    }
  };

  const handleFilterChange = (key: keyof StalenessFilters, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value === 'all' ? undefined : value,
      page: 1,
    }));
  };

  const handleValidate = async () => {
    if (!validationDialog.item || !validationDialog.action) return;

    const { item, action } = validationDialog;
    setActionLoading(item.id);

    try {
      if (action === 'dismiss') {
        const response = await dismissStaleness(item.id, 'Dismissed by user');
        if (response.success) {
          toast({
            title: 'Dismissed',
            description: 'Content marked as dismissed',
          });
        }
      } else {
        const response = await validateStaleness(item.id, {
          validationResult: action,
        });
        if (response.success) {
          toast({
            title: 'Validated',
            description: `Content marked as ${action.replace('_', ' ')}`,
          });
        }
      }
      loadQueue();
      loadStats();
    } catch (err) {
      console.error('[StalenessQueue] Error validating:', err);
      toast({
        title: 'Error',
        description: 'Failed to validate content',
        variant: 'destructive',
      });
    } finally {
      setActionLoading(null);
      setValidationDialog({ open: false, item: null, action: null });
    }
  };

  const openValidationDialog = (
    item: StalenessRecord,
    action: 'improved' | 'no_change' | 'worse' | 'dismiss'
  ) => {
    setValidationDialog({ open: true, item, action });
  };

  const getPriorityColor = (priority: number) => {
    if (priority >= 90) return 'text-red-600 font-semibold';
    if (priority >= 70) return 'text-orange-600';
    if (priority >= 50) return 'text-yellow-600';
    return 'text-gray-600';
  };

  return (
    <div className="space-y-4">
      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-5">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Pending</p>
                <p className="text-2xl font-bold">{stats.pending || 0}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-yellow-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Regenerating</p>
                <p className="text-2xl font-bold">{stats.regenerating || 0}</p>
              </div>
              <RotateCcw className="h-8 w-8 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Pending Validation</p>
                <p className="text-2xl font-bold">{stats.pending_validation || 0}</p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-purple-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Validated</p>
                <p className="text-2xl font-bold">{stats.validated || 0}</p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-green-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Dismissed</p>
                <p className="text-2xl font-bold">{stats.dismissed || 0}</p>
              </div>
              <XCircle className="h-8 w-8 text-gray-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters & Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Stale Content Queue
              <Badge variant="secondary">{pagination.total}</Badge>
            </CardTitle>
            <Button variant="outline" size="sm" onClick={loadQueue}>
              <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex gap-3 mb-4">
            <Select
              value={filters.status || 'all'}
              onValueChange={(v) => handleFilterChange('status', v)}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={filters.contentType || 'all'}
              onValueChange={(v) => handleFilterChange('contentType', v)}
            >
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Content Type" />
              </SelectTrigger>
              <SelectContent>
                {CONTENT_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Table */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              No stale content in queue
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Content</TableHead>
                  <TableHead>Knowledge</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium text-sm">
                          {item.content_type.replace('_', ' ')}
                        </p>
                        <p className="text-xs text-muted-foreground font-mono">
                          {item.content_id.slice(0, 8)}...
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="text-sm">{item.knowledge_title}</p>
                        <p className="text-xs text-muted-foreground">
                          {item.knowledge_type} • {item.scope_level}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className={getPriorityColor(item.priority)}>
                        {item.priority}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Badge className={statusColors[item.status]}>
                          {item.status.replace('_', ' ')}
                        </Badge>
                        {item.validation_result && validationIcons[item.validation_result]}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {format(new Date(item.created_at), 'MMM d, HH:mm')}
                    </TableCell>
                    <TableCell className="text-right">
                      {item.status === 'pending_validation' && (
                        <div className="flex justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0"
                            onClick={() => openValidationDialog(item, 'improved')}
                            disabled={actionLoading === item.id}
                          >
                            <ThumbsUp className="h-4 w-4 text-green-600" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0"
                            onClick={() => openValidationDialog(item, 'no_change')}
                            disabled={actionLoading === item.id}
                          >
                            <Minus className="h-4 w-4 text-gray-600" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0"
                            onClick={() => openValidationDialog(item, 'worse')}
                            disabled={actionLoading === item.id}
                          >
                            <ThumbsDown className="h-4 w-4 text-red-600" />
                          </Button>
                        </div>
                      )}
                      {item.status === 'pending' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openValidationDialog(item, 'dismiss')}
                          disabled={actionLoading === item.id}
                        >
                          <XCircle className="h-4 w-4 mr-1" />
                          Dismiss
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-4">
              <Button
                variant="outline"
                size="sm"
                disabled={pagination.page <= 1 || loading}
                onClick={() => setFilters((prev) => ({ ...prev, page: prev.page! - 1 }))}
              >
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={pagination.page >= pagination.totalPages || loading}
                onClick={() => setFilters((prev) => ({ ...prev, page: prev.page! + 1 }))}
              >
                Next
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Validation Confirmation Dialog */}
      <AlertDialog
        open={validationDialog.open}
        onOpenChange={(open) =>
          !open && setValidationDialog({ open: false, item: null, action: null })
        }
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {validationDialog.action === 'dismiss'
                ? 'Dismiss Content?'
                : 'Confirm Validation'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {validationDialog.action === 'dismiss'
                ? 'This content will not be regenerated. Are you sure?'
                : validationDialog.action === 'improved'
                ? 'Mark this regenerated content as improved?'
                : validationDialog.action === 'no_change'
                ? 'Mark this regenerated content as unchanged?'
                : 'Mark this regenerated content as worse than before?'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleValidate}>
              {actionLoading && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
